import React, { ChangeEvent, forwardRef, useEffect } from 'react';
import { FormattedMessage } from 'react-intl';
import { always, is } from 'ramda';
import { IBindingCallback1 } from '../../common/models/callback';
import { ReactComponent as CheckboxIcon } from '../../assets/icons/checkbox.svg';
import { ReactComponent as Indeterminate } from '../../assets/icons/checkbox-indeterminate.svg';
import { ReactComponent as CheckboxUnchecked } from '../../assets/icons/checkbox-unchecked.svg';
import Icon from '../Icon';
import { concat } from '../../common/helpers/arrayHelper';
import { IFormattedMessage } from '../../common/models/common/IFormattedMessage';
import styles from './styles.module.scss';

interface IProps {
  indeterminate?: boolean;
  checked?: boolean;
  radio?: boolean;
  onChange?: IBindingCallback1<ChangeEvent<HTMLInputElement>>;
  label?: IFormattedMessage | string;
  additionalText?: string;
  name?: string;
}

const Checkbox = forwardRef(({
  indeterminate,
  label,
  additionalText,
  radio,
  onChange,
  ...rest
}: IProps, ref) => {
  const defaultRef = React.useRef();
  const resolvedRef: any = ref || defaultRef;

  useEffect(() => {
    resolvedRef.current.indeterminate = indeterminate;
  }, [resolvedRef, indeterminate]);

  const getIcon = () => {
    const className = concat(
      'hr-checkbox',
      styles.icon,
      rest.checked || indeterminate ? styles.checked : ''
    );
    const uncheckedIcon = radio
      ? <Icon icon="radioUnchecked" className={className} />
      : <CheckboxUnchecked className={className} />;

    if (rest.checked) {
      return radio ? <Icon icon="radioChecked" className={className} /> : <CheckboxIcon className={className} />;
    }

    return indeterminate ? <Indeterminate className={className} /> : uncheckedIcon;
  };

  return (
    // eslint-disable-next-line jsx-a11y/label-has-associated-control
    <label className={`${styles.checkboxLabel} ${rest.checked ? 'checked-checkbox' : ''}`}>
      <span className={styles.checkboxContainer}>
        {getIcon()}
        <input
          className={styles.checkbox}
          type="checkbox"
          ref={resolvedRef}
          onChange={onChange || always(undefined)}
          {...rest}
        />
      </span>
      {label && (
        <span className={`${styles.labelText} checkbox-label-text`}>
          {is(String, label) ? label : <FormattedMessage {...(label as IFormattedMessage)} />}
          {additionalText ? ` ${additionalText}` : null}
        </span>
      )}
    </label>
  );
});

export default Checkbox;
